import { useState, useEffect, useRef, useCallback } from 'react';
import { mergeSortedMail, flagRepliedElsewhere, deadlinePassed, dropReadItems } from '../../utils/briefMerge';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Card, Spinner, ErrorMsg, EmptyState, Icon, Btn, useToast } from '../ui';
import { MessageBody } from '../shared/ChatPanel';
import { AssistantDraftCard } from '../shared/AssistantDraftCard';
import { TaskDetailModal } from '../shared/TaskDetailModal';
import { FileToDealModal } from '../shared/FileToDealModal';
import { formatInstant, isTodayInZone, isoDateInZone, todayIsoInZone } from '../../utils/format';
import { runInChunks } from '../../utils/mailThreads';

// ============================================================
// MorningBriefWidget — the caller's Daily brief on the Dashboard. (The file,
// the widget id `morningBrief` and the API routes keep the old name: the id is
// stored in people's saved dashboard layouts, and renaming it would drop the
// widget from every one of them.)
//
// WHAT IS ON IT, AND WHERE EACH PART COMES FROM
//   • the lead + mail items — Harvi's answer (GET /api/harvi/brief/latest). The
//     brief is written with an ITEMS block carrying the Graph messageId of each
//     message, checked server-side against the ids the turn actually read, so
//     every mail row can link and preview. An older, prose-only brief
//     (`structured: false`) renders as prose, as it always did.
//   • today's calendar and tasks — read LIVE, straight from Graph and SQL
//     (GET /api/harvi/brief/live), not from the model: a meeting time or a due
//     date has one right answer and no model should be paraphrasing it.
//   • the Inbox filing plan — a mailFiling draft, reviewed with the same card
//     the chat uses. Nothing moves until Save.
//
// Hovering (or focusing) a mail row opens a preview of the message, fetched on
// demand with markAsRead=false — looking is not reading. Clicking a task opens
// a small pop-up with its details and "Mark complete".
//
// It renders NOTHING for someone who has not opted in and has never had a
// brief, so shipping it default-visible leaves those dashboards unchanged.
// ============================================================

const POLL_MS = 15000;              // while a brief or a plan is being written
const LIVE_POLL_MS = 2 * 60 * 1000;  // the live reads
const LATEST_POLL_MS = 5 * 60 * 1000; // a rewrite landing from the timer
const FILING_WAIT_MS = 6 * 60 * 1000; // how long a plan build is waited on
// A plan builds itself on load, but not on every page view: each build is a
// Harvi turn. Nothing is built while a plan is waiting for review, within this
// long of the last plan, or of a build that found nothing to file.
const FILING_AUTO_GAP_MS = 30 * 60 * 1000;
const FILING_BATCH = 20;
const MAX_FILING_PAGES = 5; // mirrors MAX_FILING_PAGES in api/src/harviBrief.js
const emptyKey = id => `harvus.brief.filingEmpty.${id}`;
const readEmptyAt = id => { try { return Number(localStorage.getItem(emptyKey(id))) || 0; } catch { return 0; } };
const writeEmptyAt = (id, at) => { try { localStorage.setItem(emptyKey(id), String(at)); } catch { /* per-browser convenience */ } };

const BUCKETS = [
  { key: 'today', label: 'Needs a reply today', dot: B.red, open: true },
  { key: 'week', label: 'This week', dot: B.amber, open: true },
  { key: 'awaiting', label: 'Awaiting your reply', dot: B.navy, open: true },
  { key: 'fyi', label: 'FYI', dot: B.textLt, open: false },
];

// A section shows this many rows until "Show all" is pressed.
const SECTION_CAP = 5;

// The mail folder list, read once per page load and shared by every Move menu
// (the tooltip's and the All unread section's).
let folderListPromise = null;
function loadFolderList() {
  if (!folderListPromise) {
    folderListPromise = api.get('mail/folders').then((res) => {
      const list = (res?.folders || []).filter(f => f.id && f.path).map(f => ({ id: f.id, path: f.path }));
      list.sort((a, b) => a.path.localeCompare(b.path));
      return list;
    }).catch((err) => { folderListPromise = null; throw err; });
  }
  return folderListPromise;
}
function useMailFolders() {
  const { toast } = useToast();
  const [folders, setFolders] = useState(null);
  const load = useCallback(async () => {
    if (folders) return;
    try { setFolders(await loadFolderList()); } catch (err) {
      setFolders([]);
      toast(err?.body?.error || 'Your folders could not be read.', 'error');
    }
  }, [folders, toast]);
  return [folders, load];
}

const timeOf = (iso) => (iso ? formatInstant(iso, { hour: 'numeric', minute: '2-digit' }) : '');

// Graph returns calendar times as WALL-CLOCK strings in the requested zone,
// with no suffix — read as digits, never through new Date(), which re-zones.
function wallTime(s) {
  const m = /T(\d{2}):(\d{2})/.exec(String(s || ''));
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]}`;
}
function wallAmPm(s) {
  const m = /T(\d{2})/.exec(String(s || ''));
  return m ? (Number(m[1]) < 12 ? 'AM' : 'PM') : '';
}

const senderName = (from) => {
  if (!from) return 'Unknown sender';
  if (typeof from === 'object') return from.name || from.address || 'Unknown sender';
  return String(from).replace(/\s*<[^>]*>\s*$/, '') || 'Unknown sender';
};
const senderAddress = (from) => {
  if (!from) return '';
  if (typeof from === 'object') return from.address || '';
  const m = /<([^>]+)>/.exec(String(from));
  return m ? m[1] : String(from);
};
const initialsOf = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

function hourLabel(h) {
  const n = Number(h);
  if (!Number.isInteger(n)) return '';
  return `${n % 12 === 0 ? 12 : n % 12}:00 ${n < 12 ? 'AM' : 'PM'}`;
}

// "Today" and "yesterday" are the person's, in their saved zone.
function isToday(iso) {
  return !!iso && isTodayInZone(iso);
}

function dayLabel(iso) {
  if (!iso) return '';
  if (isToday(iso)) return timeOf(iso);
  const day = isoDateInZone(iso);
  const [ty, tm, td] = todayIsoInZone().split('-').map(Number);
  const y = new Date(Date.UTC(ty, tm - 1, td - 1)).toISOString().slice(0, 10);
  if (day === y) return `Yesterday ${timeOf(iso)}`;
  return formatInstant(iso, { month: 'short', day: 'numeric' });
}

const navigate = (detail) => window.dispatchEvent(new CustomEvent('harvi-navigate', { detail }));

// ── Small pieces ────────────────────────────────────────────────────────────

const Dot = ({ color }) => <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />;

const Pill = ({ tone = 'red', title, children }) => {
  const c = tone === 'red' ? B.red : tone === 'green' ? B.green : tone === 'navy' ? B.navy : tone === 'neutral' ? B.textMd : B.amber;
  return <span title={title} aria-label={title ? `${children}: ${title}` : undefined} style={{ fontSize: 11, fontWeight: 700, color: c, background: `${c}16`, borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>{children}</span>;
};

function DealChip({ dealId, name }) {
  if (!name) return null;
  const style = { fontSize: 11.5, fontWeight: 600, color: B.navy, background: B.tealLt, borderRadius: 999, padding: '1px 9px', whiteSpace: 'nowrap', border: 'none', fontFamily: 'inherit' };
  if (!dealId) return <span style={style}>{name}</span>;
  return (
    <button type="button" style={{ ...style, cursor: 'pointer' }} title={`Open ${name}`}
      onClick={(e) => { e.stopPropagation(); navigate({ deal: dealId }); }}>
      {name}
    </button>
  );
}

const SectionHead = ({ dot, label, count, open, onToggle }) => (
  <button type="button" onClick={onToggle} aria-expanded={open}
    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '4px 0', margin: '6px 0 4px', cursor: onToggle ? 'pointer' : 'default', fontFamily: 'inherit' }}>
    {dot && <Dot color={dot} />}
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: B.textMd }}>{label}</span>
    <span style={{ fontSize: 11, color: B.textMd, background: B.surfaceSunken || B.surfaceMuted, borderRadius: 999, padding: '0 7px' }}>{count}</span>
    {onToggle && (
      <span style={{ marginLeft: 'auto', display: 'inline-flex', color: B.textLt, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
        <Icon name="chevronRight" size={13} />
      </span>
    )}
  </button>
);

const Muted = ({ children }) => <div style={{ fontSize: 12.5, color: B.textLt, padding: '2px 0 6px' }}>{children}</div>;

// ── Mail rows with a hover preview ──────────────────────────────────────────

// One fetch per message per page load, shared by every row that shows it.
const previewCache = new Map();
function loadPreview(messageId) {
  if (!previewCache.has(messageId)) {
    previewCache.set(messageId, api.get(`mail/messages/${encodeURIComponent(messageId)}?markAsRead=false`)
      .catch((err) => { previewCache.delete(messageId); throw err; }));
  }
  return previewCache.get(messageId);
}

function htmlToText(html) {
  try {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    doc.querySelectorAll('style,script,head').forEach(n => n.remove());
    return (doc.body?.innerText || doc.body?.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  } catch { return ''; }
}

function MailPreview({ item, onFile, filed, onMarkRead, onMove, onEngage }) {
  const [detail, setDetail] = useState(null);
  const [folders, loadFolders] = useMailFolders();
  const [busy, setBusy] = useState(false);
  const act = async (fn) => { if (busy) return; setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    if (!item.verified) return undefined;
    loadPreview(item.messageId)
      .then(d => { if (live) setDetail(d); })
      .catch(() => { if (live) setError('The full message could not be loaded — showing the preview.'); });
    return () => { live = false; };
  }, [item.messageId, item.verified]);

  const body = detail ? (detail.bodyType === 'html' ? htmlToText(detail.body) : String(detail.body || '')) : (item.preview || '');
  const webLink = detail?.webLink || item.webLink;
  const atts = detail?.attachments?.filter(a => !a.isInline) || [];
  return (
    <div role="tooltip" onClick={e => e.stopPropagation()} onMouseDown={() => onEngage && onEngage()}
      style={{ position: 'absolute', left: 44, top: 'calc(100% - 2px)', zIndex: 30, width: 'min(480px, 86vw)', background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowXl || '0 8px 28px rgba(26,56,68,.18)', padding: '14px 16px', cursor: 'default', textAlign: 'left' }}>
      <div style={{ fontWeight: 700, fontSize: 13.5, color: B.text }}>{detail?.subject || item.subject || '(no subject)'}</div>
      <div style={{ fontSize: 12, color: B.textLt, margin: '2px 0 10px' }}>
        {senderName(detail?.from || item.from)}{senderAddress(detail?.from || item.from) ? ` <${senderAddress(detail?.from || item.from)}>` : ''}
        {' · '}{dayLabel(detail?.receivedAt || item.receivedAt)}
      </div>
      <div style={{ fontSize: 13, color: B.text, borderLeft: `3px solid ${B.teal}`, paddingLeft: 10, maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
        {!detail && !error && item.verified ? (item.preview ? `${item.preview}…` : 'Loading…') : (body.slice(0, 2500) || '(no text)')}
      </div>
      {error && <div style={{ fontSize: 11.5, color: B.textLt, marginTop: 6 }}>{error}</div>}
      {atts.length > 0 && (
        <div style={{ fontSize: 12, color: B.textMd, marginTop: 8 }}>
          <Icon name="attach" size={12} /> {atts.map(a => a.name).join(', ')}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
        {webLink && (
          <a href={webLink} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12.5, fontWeight: 600, color: '#fff', background: B.navy, borderRadius: 8, padding: '6px 12px', textDecoration: 'none' }}>
            Open in Outlook
          </a>
        )}
        {onFile && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onFile(); }}
            style={{ fontSize: 12.5, fontWeight: 600, color: B.navy, background: B.tealLt, border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
            {filed ? `Filed to ${filed}` : (item.dealId && item.dealName ? `File to ${item.dealName}…` : 'File to a deal…')}
          </button>
        )}
        {onMarkRead && (
          <button type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); act(onMarkRead); }}
            title={item.threadCount > 1 ? `Mark the ${item.threadCount} unread messages in this thread read in Outlook` : 'Mark this email read in Outlook'}
            style={{ fontSize: 12.5, fontWeight: 600, color: B.navy, background: B.surface, border: `1px solid ${B.border}`, borderRadius: 8, padding: '6px 12px', cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            Mark read
          </button>
        )}
        {onMove && (
          <MoveMenu label="Move to…" folders={folders} onOpen={loadFolders} disabled={busy}
            onPick={(id, label) => act(() => onMove(id, label))} />
        )}
        <button type="button" onClick={() => navigate({ view: 'mail' })}
          style={{ fontSize: 12.5, fontWeight: 600, color: B.text, background: B.surface, border: `1px solid ${B.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
          Go to Mail
        </button>
      </div>
    </div>
  );
}

function MailRow({ item, onMarkRead, onMove }) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const timer = useRef(null);
  const show = hover || pinned;
  const enter = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setHover(true), 220); };
  const leave = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setHover(false), 150); };
  useEffect(() => () => clearTimeout(timer.current), []);
  const name = senderName(item.from);
  // Filing opens a dialog OUTSIDE the hover card, which unmounts the moment
  // the pointer leaves it. `filed` is the deal's label once it went through.
  const [filing, setFiling] = useState(false);
  const [filed, setFiled] = useState(null);

  return (
    <>
    <div tabIndex={0} role="button" aria-expanded={show}
      onMouseEnter={enter} onMouseLeave={leave}
      onFocus={() => setHover(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) { setHover(false); setPinned(false); } }}
      onClick={() => setPinned(p => !p)}
      onKeyDown={(e) => { if (e.key === 'Escape') { setPinned(false); setHover(false); } }}
      style={{ position: 'relative', display: 'grid', gridTemplateColumns: '34px 1fr', gap: 10, padding: 10, margin: '0 -10px', borderRadius: 10, cursor: 'pointer', background: show ? B.surfaceMuted : 'transparent', outline: 'none' }}>
      <div style={{ width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, background: B.tealLt, color: B.navyDk }}>
        {initialsOf(name)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13.5, fontWeight: 600, color: B.text }}>
          <span style={{ minWidth: 0 }}>{item.subject || item.summary || '(no subject)'}</span>
          {item.groupLabel && <Pill tone={item.bucket === 'today' ? 'red' : 'amber'}>{item.groupLabel}</Pill>}
          {item.deadline && (deadlinePassed(item.deadline)
            ? <Pill tone="neutral" title="This deadline has passed">{item.deadline} · passed</Pill>
            : <Pill tone={item.bucket === 'today' ? 'red' : 'amber'}>{item.deadline}</Pill>)}
          {item.repliedElsewhere && (
            <Pill tone="green" title={`You wrote to them on ${new Date(item.repliedElsewhere.at).toLocaleString()} — "${item.repliedElsewhere.subject}"`}>
              You replied in another thread
            </Pill>
          )}
          {item.isNew && <Pill tone="green" title={item.newNote || 'Arrived since the last version of the brief'}>New</Pill>}
          {item.youWroteLast && <Pill tone="amber">You wrote last</Pill>}
          {item.fromSort && <Pill tone="navy" title="Sorted from your unread mail by the background sort — not in Harvi's written brief">Sorted</Pill>}
          {item.fromSort && item.unreadCount > 1 && <Pill tone="navy">{item.unreadCount} unread</Pill>}
          {filed && <Pill tone="green">Filed</Pill>}
          <DealChip dealId={item.dealId} name={item.dealName} />
        </div>
        {item.summary && item.subject && <div style={{ fontSize: 13, color: B.textMd }}>{item.summary}</div>}
        <div style={{ fontSize: 12, color: B.textLt }}>
          {name}{item.receivedAt ? ` · ${dayLabel(item.receivedAt)}` : ''}{item.hasAttachments ? ' · attachment' : ''}
        </div>
      </div>
      {show && item.verified && <MailPreview item={item} filed={filed}
        onEngage={() => setPinned(true)}
        onMarkRead={onMarkRead ? () => onMarkRead(item) : null}
        onMove={onMove ? (id, label) => onMove(item, id, label) : null}
        onFile={() => { setHover(false); setPinned(false); setFiling(true); }} />}
    </div>
    {filing && (
      <FileToDealModal
        messageId={item.messageId}
        subject={item.subject || item.summary}
        suggestedDealId={item.dealId || null}
        onClose={() => setFiling(false)}
        onFiled={(r) => setFiled(r?.label || item.dealName || 'deal')}
      />
    )}
    </>
  );
}

// ── The widget ──────────────────────────────────────────────────────────────

export function MorningBriefWidget({ users = [] }) {
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi(() => api.get('harvi/brief/latest'), []);
  const [live, setLive] = useState(null);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [openBuckets, setOpenBuckets] = useState(() => Object.fromEntries(BUCKETS.map(b => [b.key, b.open])));
  const [taskId, setTaskId] = useState(null);
  // Sections show SECTION_CAP rows until "Show all" is pressed.
  const [showAll, setShowAll] = useState({});
  // Rows marked read or moved from their preview leave the brief at once.
  const [clearedMail, setClearedMail] = useState(() => new Set());

  const brief = data?.brief || null;
  // Every unread email (GET harvi/brief/unread) — the rest of the Inbox beyond
  // what the brief lists, grouped by Harvi's stored verdict.
  const [unread, setUnread] = useState(null);
  const loadUnread = useCallback(async () => {
    try { setUnread(await api.get('harvi/brief/unread', { timeout: 60000 })); } catch { /* keep the last read */ }
  }, []);
  const writing = running || (brief && !brief.ready && isToday(brief.createdAt));

  useEffect(() => {
    if (!writing || running) return undefined;
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [writing, running, reload]);

  const active = !!data && (data.enabled || !!brief) && data.connected && data.personalEnabled;

  const loadLive = useCallback(async () => {
    try { setLive(await api.get('harvi/brief/live')); } catch { /* keep the last good read */ }
  }, []);
  // Sync — re-read today's mail and calendar straight from Outlook now, instead
  // of waiting for the 2-minute poll. It does NOT rewrite the brief ("Update
  // now" does that, and takes a minute or two).
  const [syncing, setSyncing] = useState(false);
  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await api.get('harvi/brief/live').then(setLive);
      reload();
      loadUnread();
    } catch { toast('Could not reach Outlook — showing the last read.', 'error'); }
    finally { setSyncing(false); }
  };
  useEffect(() => {
    if (!active) return undefined;
    loadLive();
    loadUnread();
    const tick = (fn, ms) => setInterval(() => { if (document.visibilityState === 'visible') fn(); }, ms);
    const a = tick(loadLive, LIVE_POLL_MS);
    const b = tick(reload, LATEST_POLL_MS);
    return () => { clearInterval(a); clearInterval(b); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, brief?.updatedAt]);

  const runNow = async () => {
    setRunning(true);
    try {
      // A multi-minute turn whose response can outlive the edge, so a timeout
      // here is not a failure — the reload below finds the result.
      const res = await api.post('harvi/brief/refresh', {}, { timeout: 300000 });
      if (res?.skipped) toast(res.reason || 'The brief could not run.', 'error');
    } catch { /* the thread row is the outcome; reload decides */ }
    finally { setRunning(false); reload(); loadLive(); loadUnread(); }
  };

  const openInHarvi = () => {
    if (!brief) return;
    window.dispatchEvent(new CustomEvent('harvi-navigate', { detail: { conversation: brief.conversationId } }));
  };

  if (loading && !data) return <Card><Spinner /></Card>;
  if (error) return <Card><ErrorMsg msg={error} /></Card>;
  if (!data) return null;
  // Not a brief user: stay out of the way.
  if (!data.enabled && !brief) return null;

  // Harvi's written items, plus every unread thread already SORTED as needing
  // you that the brief did not mention — it reads a handful of threads a turn,
  // the stored verdicts cover the whole unread Inbox. See utils/briefMerge.js.
  const written = brief?.structured ? (brief.items || []) : [];
  const merged = brief?.structured && unread?.available
    ? flagRepliedElsewhere(mergeSortedMail(dropReadItems(written, unread), unread.rows), unread.rows) : written;
  const sortedAdded = merged.length - written.length;
  // Every unread message in an item's thread, so "Mark read" / "Move" act on
  // the thread, not only the one message the brief names.
  const threadIdsFor = (item) => {
    const row = (unread?.rows || []).find(r => r.messageId === item.messageId
      || (item.conversationId && r.conversationId === item.conversationId));
    return [...new Set([item.messageId, ...(row?.messageIds || [])].filter(Boolean))];
  };
  const items = merged.filter(i => !clearedMail.has(i.messageId))
    .map(i => ({ ...i, threadCount: threadIdsFor(i).length }));
  const clearItem = (item) => setClearedMail(prev => new Set(prev).add(item.messageId));
  const actOnThread = async (item, body, done) => {
    const ids = threadIdsFor(item);
    let failed = 0;
    await runInChunks(ids, 4, async (id) => {
      try { await api.put(`mail/messages/${encodeURIComponent(id)}`, body); } catch { failed += 1; }
    });
    if (failed === ids.length) { toast('Outlook did not accept that — nothing changed.', 'error'); return; }
    clearItem(item);
    if (failed) toast(`${failed} of ${ids.length} message(s) could not be changed.`, 'error');
    else toast(done(ids.length), 'success');
    loadUnread();
  };
  const markItemRead = (item) => actOnThread(item, { isRead: true },
    n => `Marked ${n === 1 ? 'it' : `${n} messages`} read.`);
  const moveItem = (item, folderId, label) => actOnThread(item, { move: folderId },
    n => `Moved ${n === 1 ? 'it' : `${n} messages`} to ${label}.`);
  // Unread mail that arrived after this version of the brief and is not in it.
  const inBrief = new Set(items.flatMap(i => [i.messageId, ...((unread?.rows || []).find(r => r.messageId === i.messageId)?.messageIds || [])]));
  const arrived = (live?.mail?.items || []).filter(m => !inBrief.has(m.messageId) && !clearedMail.has(m.messageId))
    .map(m => ({ ...m, verified: true, isNew: true, bucket: 'new' }));
  const events = (live?.calendar?.items || []).filter(e => !e.cancelled);
  const upcoming = events.filter(e => !e.past);
  const tasksDue = live?.tasks?.due || [];
  const tasksNew = live?.tasks?.added || [];
  // Threads whose sender you have written to since, in another thread: out of
  // the "needs you" sections, into their own (closed) one. See briefMerge.js.
  const handled = items.filter(i => i.repliedElsewhere);
  const needsItems = items.filter(i => !i.repliedElsewhere);
  const counts = Object.fromEntries(BUCKETS.map(b => [b.key, needsItems.filter(i => i.bucket === b.key).length]));

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 18px', borderBottom: `1px solid ${B.divider || B.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', color: B.navy }}><Icon name="sparkles" size={16} /></span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: B.text }}>Daily brief</div>
          <div style={{ fontSize: 12, color: B.textMd }}>
            {brief
              ? (brief.isToday
                  ? `${formatInstant(new Date(), { weekday: 'long', month: 'short', day: 'numeric' })} · updated ${timeOf(brief.updatedAt || brief.createdAt)}`
                  : formatInstant(brief.createdAt, { weekday: 'short', month: 'short', day: 'numeric' }))
              : data.enabled ? `Arrives daily at ${hourLabel(data.hour)}` : ''}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {brief?.ready && <Btn variant="ghost" size="sm" onClick={openInHarvi}>Open in Harvi</Btn>}
        {active && (
          <Btn variant="ghost" size="sm" onClick={syncNow} loading={syncing} disabled={syncing}
            title="Re-read today's mail and calendar from Outlook now">
            <Icon name="refresh" size={13} /> Sync
          </Btn>
        )}
        {data.connected && data.personalEnabled && (
          <Btn variant="ghost" size="sm" onClick={runNow} disabled={writing}>
            {writing ? 'Writing…' : brief?.isToday ? 'Update now' : 'Run now'}
          </Btn>
        )}
      </div>
    </div>
  );

  let body;
  if (!data.personalEnabled) {
    body = <EmptyState icon="info" title="Not available" message="Mail and calendar are not enabled on this deployment, so no brief can be written." />;
  } else if (!data.connected) {
    body = <EmptyState icon="info" title="Connect your mail"
      message="The brief reads your Outlook mail and calendar. Connect them under Settings → Integrations." />;
  } else if (writing) {
    body = <Spinner label="Harvi is reading your mail and calendar — this takes a minute or two." />;
  } else if (!brief) {
    body = <EmptyState icon="info" title="No brief yet" message={`Your first brief arrives at ${hourLabel(data.hour)}, or run one now.`} />;
  } else if (!brief.ready) {
    body = <EmptyState icon="info" title="That brief didn't finish" message="Harvi started it but no answer landed. Run it again." />;
  } else if (!brief.structured) {
    // A brief written before the ITEMS block: prose, as it always was.
    body = (
      <div>
        <div style={{ position: 'relative', padding: '14px 18px', maxHeight: expanded ? 'none' : 340, overflow: 'hidden' }}>
          <MessageBody text={brief.text} />
          {!expanded && <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 60, background: `linear-gradient(transparent, ${B.surface})`, pointerEvents: 'none' }} />}
        </div>
        <div style={{ padding: '6px 18px 12px', textAlign: 'center' }}>
          <button onClick={() => setExpanded(v => !v)}
            style={{ background: 'none', border: 'none', color: B.navy, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            {expanded ? 'Show less' : 'Show the whole brief'}
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        {brief.lead && <LeadPanel lead={brief.lead} unread={unread} />}
        <div style={{ padding: '4px 18px 8px' }}>
          {BUCKETS.map(b => {
            const list = needsItems.filter(i => i.bucket === b.key);
            if (!list.length) return null;
            const open = openBuckets[b.key];
            return (
              <div key={b.key}>
                <SectionHead dot={b.dot} label={b.label} count={list.length} open={open}
                  onToggle={() => setOpenBuckets(s => ({ ...s, [b.key]: !s[b.key] }))} />
                {open && (
                  <CappedList items={list} all={!!showAll[b.key]}
                    onToggle={() => setShowAll(s => ({ ...s, [b.key]: !s[b.key] }))}
                    render={i => <MailRow key={i.messageId} item={i} onMarkRead={markItemRead} onMove={moveItem} />} />
                )}
              </div>
            );
          })}
          {!needsItems.length && <Muted>Nothing in your unread mail needs you.</Muted>}
          {handled.length > 0 && (
            <div>
              <SectionHead dot={B.green} label="You've written to them since" count={handled.length}
                open={!!openBuckets.handled} onToggle={() => setOpenBuckets(s => ({ ...s, handled: !s.handled }))} />
              {openBuckets.handled && (
                <>
                  <div style={{ fontSize: 12, color: B.textLt, padding: '2px 0 4px' }}>
                    You sent the sender mail in another thread after these arrived — often the same matter under a new subject. Check, then mark read or file.
                  </div>
                  <CappedList items={handled} all={!!showAll.handled}
                    onToggle={() => setShowAll(s => ({ ...s, handled: !s.handled }))}
                    render={i => <MailRow key={i.messageId} item={i} onMarkRead={markItemRead} onMove={moveItem} />} />
                </>
              )}
            </div>
          )}
          {sortedAdded > 0 && (
            <div style={{ fontSize: 12, color: B.textLt, padding: '4px 0 2px' }}>
              {sortedAdded} marked <b>Sorted</b> {sortedAdded === 1 ? 'was' : 'were'} judged from your unread mail by the background sort, not read by Harvi in this brief.
            </div>
          )}
          {arrived.length > 0 && (
            <div>
              <SectionHead dot={B.green} label="Arrived since this version" count={arrived.length} open />
              <CappedList items={arrived} all={!!showAll.arrived}
                onToggle={() => setShowAll(s => ({ ...s, arrived: !s.arrived }))}
                render={i => <MailRow key={i.messageId} item={i} onMarkRead={markItemRead} onMove={moveItem} />} />
            </div>
          )}
        </div>
      </>
    );
  }

  const ready = active && brief?.ready;
  return (
    <Card style={{ padding: 0, overflow: 'visible' }}>
      {header}
      {ready && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 18px', borderBottom: `1px solid ${B.divider || B.border}`, background: B.surfaceMuted }}>
          {live && <StatChip dot={B.navy} n={upcoming.length} label={upcoming.length === 1 ? 'meeting left' : 'meetings left'} />}
          {live && <StatChip dot={B.green} n={tasksDue.length + tasksNew.length} label="tasks" />}
          {brief.structured && BUCKETS.map(b => (
            <StatChip key={b.key} dot={b.dot} n={counts[b.key]} label={b.key === 'today' ? 'reply today' : b.key === 'week' ? 'this week' : 'FYI'} />
          ))}
        </div>
      )}
      {active && live && <LiveBlock live={live} events={events} tasksDue={tasksDue} tasksNew={tasksNew}
        showMail={!brief?.structured} sinceBrief={!!brief?.isToday} onTask={setTaskId} />}
      {body}
      {active && unread?.available && <UnreadSection unread={unread} onChanged={loadUnread} />}
      {active && brief && <FilingSection conversationId={brief.conversationId} filingConversationId={brief.filingConversationId || null} inFlight={!!brief.filingInFlight} />}
      {taskId && <TaskDetailModal taskId={taskId} users={users} onClose={() => setTaskId(null)} onChanged={loadLive} />}
    </Card>
  );
}

// The first SECTION_CAP rows, then a button for the rest of the group.
function CappedList({ items, all, onToggle, render }) {
  const shown = all ? items : items.slice(0, SECTION_CAP);
  const hidden = items.length - SECTION_CAP;
  return (
    <>
      {shown.map(render)}
      {hidden > 0 && (
        <button type="button" onClick={onToggle}
          style={{ background: 'none', border: 'none', color: B.navy, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0 8px' }}>
          {all ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </>
  );
}

const StatChip = ({ dot, n, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.surface, border: `1px solid ${B.divider || B.border}`, borderRadius: 999, padding: '3px 11px', fontSize: 12.5, color: B.text }}>
    <Dot color={dot} /><b style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</b> {label}
  </span>
);

// ── The lead: Harvi's summary, full width, with the Inbox's real numbers ────
// The counts come from the unread listing, not the model: "129 unread" has one
// right answer and the model only saw the pages it read.
function LeadPanel({ lead, unread }) {
  const c = unread?.available ? unread.counts : null;
  const needs = c ? c.today + c.week + c.awaiting : 0;
  return (
    <div style={{ margin: '14px 18px 4px', padding: '12px 14px', background: B.surfaceMuted, borderLeft: `3px solid ${B.navy}`, borderRadius: 10 }}>
      <div style={{ fontSize: 13.5, color: B.text, lineHeight: 1.5 }}>
        <MessageBody text={lead} />
      </div>
      {c && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8, fontSize: 12.5, color: B.textMd }}>
          <span><b style={{ color: B.text }}>{unread.unreadTotal}</b> unread</span>
          <span><b style={{ color: B.red }}>{needs}</b> need you</span>
          <span><b style={{ color: B.text }}>{c.fyi}</b> FYI</span>
          <span><b style={{ color: B.text }}>{c.none}</b> nothing needed</span>
          {c.unsorted > 0 && <span><b style={{ color: B.amber }}>{c.unsorted}</b> not sorted yet</span>}
          <span style={{ color: B.textLt }}>· threads, not messages</span>
        </div>
      )}
    </div>
  );
}

// ── All unread: the whole Inbox, not just what the brief picked ─────────────
// One row per thread with Harvi's stored verdict. "Sort the rest" runs the
// background mail judge over threads nothing has judged yet, a batch at a time;
// "Mark read" clears a thread (or a whole group) in Outlook. Nothing is moved
// or deleted.
const UNREAD_GROUPS = [
  { id: 'needs', label: 'Needs you', dot: B.red, of: ['today', 'week', 'awaiting'] },
  { id: 'fyi', label: 'FYI', dot: B.textLt, of: ['fyi'] },
  { id: 'none', label: 'Nothing needed', dot: B.border, of: ['none'] },
  { id: 'unsorted', label: 'Not sorted yet', dot: B.amber, of: ['unsorted'] },
];
const GROUP_LABEL = { today: 'Today', week: 'This week', awaiting: 'Awaiting you', fyi: 'FYI', none: 'Nothing needed' };

function UnreadSection({ unread, onChanged }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('needs');
  const [sorting, setSorting] = useState(null); // { done, left }
  const [cleared, setCleared] = useState(() => new Set());
  const [marking, setMarking] = useState(false);

  const rows = (unread.rows || []).filter(r => !cleared.has(r.messageId));
  const inGroup = (g) => rows.filter(r => g.of.includes(r.group));
  const current = UNREAD_GROUPS.find(g => g.id === tab) || UNREAD_GROUPS[0];
  const list = inGroup(current);

  const markRead = async (targets) => {
    const ids = targets.flatMap(r => r.messageIds || [r.messageId]);
    let failed = 0;
    await runInChunks(ids, 4, async (id) => {
      try { await api.put(`mail/messages/${encodeURIComponent(id)}`, { isRead: true }); } catch { failed += 1; }
    });
    setCleared(prev => { const n = new Set(prev); targets.forEach(r => n.add(r.messageId)); return n; });
    if (failed) toast(`${failed} message(s) could not be marked read.`, 'error');
    return failed;
  };

  // MOVE: the folder list is read once, the first time a Move menu is opened.
  const [folders, loadFolders] = useMailFolders();
  const [moving, setMoving] = useState(false);
  const moveThreads = async (targets, folderId, label) => {
    const ids = targets.flatMap(r => r.messageIds || [r.messageId]);
    let failed = 0;
    setMoving(true);
    try {
      await runInChunks(ids, 4, async (id) => {
        try { await api.put(`mail/messages/${encodeURIComponent(id)}`, { move: folderId }); } catch { failed += 1; }
      });
    } finally { setMoving(false); }
    setCleared(prev => { const n = new Set(prev); targets.forEach(r => n.add(r.messageId)); return n; });
    if (failed) toast(`${failed} message(s) could not be moved.`, 'error');
    else toast(`Moved ${ids.length} message${ids.length === 1 ? '' : 's'} to ${label}.`, 'success');
  };
  const moveGroup = (folderId, label) => {
    if (!list.length) return;
    const msgs = list.reduce((n, r) => n + (r.unreadCount || 1), 0);
    if (!window.confirm(`Move ${msgs} message${msgs === 1 ? '' : 's'} in ${list.length} thread${list.length === 1 ? '' : 's'} to ${label}?`)) return;
    moveThreads(list, folderId, label);
  };

  const markGroup = async () => {
    if (!list.length) return;
    const msgs = list.reduce((n, r) => n + (r.unreadCount || 1), 0);
    if (!window.confirm(`Mark ${msgs} message${msgs === 1 ? '' : 's'} in ${list.length} thread${list.length === 1 ? '' : 's'} as read? Nothing is moved or deleted.`)) return;
    setMarking(true);
    try {
      const failed = await markRead(list);
      if (!failed) toast(`Marked ${msgs} as read.`, 'success');
    } finally { setMarking(false); }
  };

  // Sort batch by batch until nothing is left or a batch makes no progress.
  const sortRest = async () => {
    let pending = rows.filter(r => r.group === 'unsorted' && r.conversationId).map(r => r.conversationId);
    const total = pending.length;
    let done = 0;
    setSorting({ done, total });
    try {
      while (pending.length) {
        const batch = pending.slice(0, unread.sortMax || 24);
        pending = pending.slice(batch.length);
        // eslint-disable-next-line no-await-in-loop
        const res = await api.post('harvi/brief/unread/sort', { conversationIds: batch }, { timeout: 90000 });
        done += res?.judged || 0;
        setSorting({ done, total });
        if (!res?.judged) break;
      }
    } catch (err) {
      toast(err?.body?.error || err?.message || 'Sorting stopped — try again in a minute.', 'error');
    } finally {
      setSorting(null);
      onChanged();
    }
  };

  const unsortedCount = inGroup(UNREAD_GROUPS[3]).length;

  // SORTING STARTS ON ITS OWN, once per mount: a pile of "Not sorted yet" is
  // not something a person should have to press through 24 at a time. The
  // background sync sorts it too (every 15 minutes), so closing the page does
  // not stop the work, only this faster pass.
  const autoSorted = useRef(false);
  useEffect(() => {
    if (autoSorted.current || sorting || !unsortedCount) return;
    autoSorted.current = true;
    sortRest();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsortedCount]);
  return (
    <div style={{ padding: '4px 18px 12px', borderTop: `1px solid ${B.divider || B.border}` }}>
      <SectionHead dot={B.navy} label="All unread" count={rows.length} open={open} onToggle={() => setOpen(v => !v)} />
      {!open && (
        <Muted>
          Every unread thread in your Inbox, sorted by Harvi — {unread.unreadTotal} unread message{unread.unreadTotal === 1 ? '' : 's'}.
          {sorting ? ` Sorting… ${sorting.done} of ${sorting.total}.` : unsortedCount > 0 ? ` ${unsortedCount} not sorted yet.` : ''}
          {' '}Open it to mark threads read or move them to a folder.
        </Muted>
      )}
      {open && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 8px' }}>
            {UNREAD_GROUPS.map(g => {
              const n = inGroup(g).length;
              const on = g.id === tab;
              return (
                <button key={g.id} type="button" onClick={() => setTab(g.id)} aria-pressed={on}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 11px', borderRadius: 999, fontSize: 12.5, fontFamily: 'inherit', cursor: 'pointer',
                    border: `1px solid ${on ? B.navy : (B.divider || B.border)}`, background: on ? B.navy : B.surface, color: on ? '#fff' : B.text }}>
                  <Dot color={g.dot} /><b style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</b> {g.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            {tab === 'unsorted' && unsortedCount > 0 && (
              <Btn size="sm" onClick={sortRest} loading={!!sorting} disabled={!!sorting}>
                {sorting ? `Sorting… ${sorting.done} of ${sorting.total}` : `Sort these ${unsortedCount} with Harvi`}
              </Btn>
            )}
            {tab !== 'needs' && list.length > 0 && (
              <Btn size="sm" variant="ghost" onClick={markGroup} loading={marking} disabled={marking}>
                Mark all {list.length} as read
              </Btn>
            )}
            {tab !== 'needs' && list.length > 0 && (
              <MoveMenu label={`Move all ${list.length} to…`} folders={folders} onOpen={loadFolders}
                disabled={moving} onPick={moveGroup} />
            )}
            {unread.truncated && <span style={{ fontSize: 12, color: B.textLt }}>Showing the newest {unread.listed} unread messages.</span>}
          </div>
          {!list.length && <Muted>{tab === 'unsorted' ? 'Everything unread has been sorted.' : 'Nothing here.'}</Muted>}
          {list.map(r => (
            <div key={r.messageId} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <MailRow item={{
                  messageId: r.messageId, subject: r.unreadCount > 1 ? `${r.subject} (${r.unreadCount})` : r.subject,
                  from: r.from, receivedAt: r.receivedAt, hasAttachments: r.hasAttachments, webLink: r.webLink,
                  summary: r.summary || (r.earlier?.summary ? `Earlier: ${r.earlier.summary}` : r.preview),
                  dealName: r.dealName, dealId: r.dealId, deadline: r.deadline, bucket: r.group, verified: true,
                  groupLabel: tab === 'needs' ? GROUP_LABEL[r.group] : null,
                  threadCount: (r.messageIds || [r.messageId]).length,
                  repliedElsewhere: r.repliedElsewhere || null,
                }} onMarkRead={() => markRead([r])} onMove={(_, id, label) => moveThreads([r], id, label)} />
              </div>
              <div style={{ marginTop: 12, flexShrink: 0, display: 'flex', gap: 6 }}>
                <button type="button" title="Mark this thread read in Outlook" onClick={() => markRead([r])}
                  style={{ fontSize: 12, fontFamily: 'inherit', color: B.navy, background: 'none', border: `1px solid ${B.divider || B.border}`, borderRadius: 8, padding: '2px 8px', cursor: 'pointer' }}>
                  Mark read
                </button>
                <MoveMenu label="Move" folders={folders} onOpen={loadFolders} disabled={moving}
                  onPick={(id, label) => moveThreads([r], id, label)} />
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// A folder picker: a select whose options load the first time it is focused.
// Archive leads (Graph accepts the well-known name as a destination).
function MoveMenu({ label, folders, onOpen, onPick, disabled }) {
  return (
    <select value="" disabled={disabled} aria-label={label}
      onFocus={onOpen} onMouseDown={onOpen}
      onChange={(e) => {
        const id = e.target.value;
        if (!id) return;
        const name = id === 'archive' ? 'Archive' : ((folders || []).find(f => f.id === id)?.path || 'that folder');
        onPick(id, name);
      }}
      style={{ fontSize: 12, fontFamily: 'inherit', color: B.navy, background: B.surface, border: `1px solid ${B.divider || B.border}`, borderRadius: 8, padding: '2px 6px', cursor: 'pointer', maxWidth: 200 }}>
      <option value="">{label}</option>
      <option value="archive">Archive</option>
      {folders === null && <option disabled>Loading folders…</option>}
      {(folders || []).filter(f => !/^archive$/i.test(f.path)).map(f => <option key={f.id} value={f.id}>{f.path}</option>)}
    </select>
  );
}

// ── Today: calendar + tasks (live) ──────────────────────────────────────────
// Rendered ABOVE the mail: the day's meetings and tasks lead the brief.
// Straight reads, no model. Each section reports its own failure rather than
// hiding. For an older prose brief the new-mail list is shown here too, since
// that brief has no mail rows of its own.

function LiveBlock({ live, events, tasksDue, tasksNew, showMail, sinceBrief, onTask }) {
  const mail = live.mail || {};
  const cal = live.calendar || {};
  const tasks = live.tasks || {};
  const nowWall = (() => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })();
  const isNow = e => !e.past && !e.allDay && String(e.start || '').slice(11, 16) <= nowWall;

  return (
    <div style={{ borderBottom: `1px solid ${B.divider || B.border}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 0 }}>
        <div style={{ padding: '6px 18px 12px' }}>
          <SectionHead dot={B.navy} label="Today" count={events.length} />
          {cal.error ? <Muted>{cal.error}</Muted>
            : events.length ? events.map(e => {
                const content = (
                  <div style={{ display: 'grid', gridTemplateColumns: '58px 1fr', gap: 10, padding: '6px 8px', margin: '0 -8px', borderRadius: 9, opacity: e.past ? 0.5 : 1 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: isNow(e) ? B.green : B.textMd, fontVariantNumeric: 'tabular-nums' }}>
                      {e.allDay ? 'All day' : <>{wallTime(e.start)} <span style={{ fontSize: 10.5 }}>{wallAmPm(e.start)}</span></>}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, fontWeight: 600, color: B.text }}>
                        <span>{e.subject}</span>
                        {e.changed && <Pill tone="amber">Changed</Pill>}
                        {isNow(e) && <Pill tone="green">Now</Pill>}
                      </div>
                      {e.location && <div style={{ fontSize: 12, color: B.textLt }}>{e.location}</div>}
                    </div>
                  </div>
                );
                return e.webLink
                  ? <a key={e.eventId} href={e.webLink} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none' }}>{content}</a>
                  : <div key={e.eventId}>{content}</div>;
              })
            : <Muted>Nothing on the calendar today.</Muted>}
        </div>
        <div style={{ padding: '6px 18px 12px' }}>
          <SectionHead dot={B.green} label="Tasks" count={tasksDue.length + tasksNew.length} />
          {tasks.error ? <Muted>{tasks.error}</Muted>
            : (tasksDue.length || tasksNew.length) ? [...tasksDue.map(t => ({ ...t, isNew: false })), ...tasksNew.map(t => ({ ...t, isNew: true }))].map(t => {
                const today = todayIsoInZone();
                return (
                  <button key={t.taskId} type="button" onClick={() => onTask(t.taskId)}
                    style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 10, width: 'calc(100% + 16px)', textAlign: 'left', background: 'none', border: 'none', padding: '6px 8px', margin: '0 -8px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={ev => { ev.currentTarget.style.background = B.surfaceMuted; }}
                    onMouseLeave={ev => { ev.currentTarget.style.background = 'none'; }}>
                    <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: `${B.amber}18`, color: B.amber }}>
                      <Icon name="check" size={14} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, fontWeight: 600, color: B.text }}>
                        <span>{t.title}</span>
                        {t.isNew && <Pill tone="green">New</Pill>}
                        {t.dueDate && t.dueDate < today && <Pill tone="red">Overdue</Pill>}
                        {t.dueDate === today && <Pill tone="red">Due today</Pill>}
                      </div>
                      <div style={{ fontSize: 12, color: B.textLt }}>
                        {[t.dealName || 'No deal', t.dueDate && t.dueDate > today ? `due ${t.dueDate}` : null].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </button>
                );
              })
            : <Muted>No new tasks, nothing due today.</Muted>}
        </div>
      </div>
      {showMail && (
        <div style={{ padding: '0 18px 10px' }}>
          <SectionHead dot={B.green} label={sinceBrief ? `New unread since ${timeOf(live.since)}` : 'New unread today'} count={mail.items?.length || 0} />
          {mail.error ? <Muted>{mail.error}</Muted>
            : mail.items?.length ? mail.items.map(m => <MailRow key={m.messageId} item={{ ...m, verified: true }} />)
            : <Muted>Nothing new.</Muted>}
        </div>
      )}
    </div>
  );
}

// ── Inbox filing plan (collapsed) ────────────────────────────────────────────
// A folder plan for READ mail still in the Inbox. It is Harvi's ordinary
// mailFiling draft, reviewed with the same card the chat uses — nothing moves
// until Save. Built by the widget itself (on load, and again after each Save),
// never by the 7am timer: a draft expires an hour after it is proposed, so one
// built at 7am would be dead before it was read.
//
// ONE BUILD AT A TIME. The build outlives the ~45 s edge, so a request that
// "failed" is usually still running. The section therefore keeps waiting after
// an error — polling for a plan newer than the press — rather than handing the
// button back, and the server refuses a second build while one is in flight.

function FilingSection({ conversationId, filingConversationId, inFlight }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [buildStartedAt, setBuildStartedAt] = useState(null);

  // What the last Save did — kept on screen while the next plan builds, since
  // it lists the messages that could not be moved.
  const [lastFiled, setLastFiled] = useState(null);
  const [nothingLeft, setNothingLeft] = useState(false);
  // What the last build that proposed nothing actually did (the server reads
  // it off the turn): Harvi's reply, how many read messages it looked at, and
  // any refusal from propose_mail_filing. Shown instead of a guess.
  const [noPlanWhy, setNoPlanWhy] = useState(null);

  // Plans are built in a filing thread of their own; plans built before that
  // live in the brief thread, so both are read. The filing thread's id comes
  // from the brief read, or from the build response on today's first build.
  const [filingId, setFilingId] = useState(filingConversationId);
  useEffect(() => { if (filingConversationId) setFilingId(filingConversationId); }, [filingConversationId]);
  const loadDrafts = useCallback(async (extraId) => {
    const ids = [...new Set([extraId, filingId, conversationId].filter(id => typeof id === 'string' && id))];
    const lists = await Promise.all(ids.map(id => api.get(`assistant/drafts?conversationId=${id}`)
      .then(res => res?.drafts || []).catch(() => [])));
    const list = lists.flat().filter(d => d.kind === 'mailFiling');
    setDrafts(list);
    return list;
  }, [conversationId, filingId]);
  // Loaded on mount, not on open: the section decides for itself whether the
  // next plan is owed, so it needs to know what is already there.
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  // A build the server says is running (a reload mid-build, another tab).
  // Consumed ONCE: `inFlight` comes from a read that may be minutes old, and
  // re-arming on it after the plan landed would wait forever.
  const tookInFlight = useRef(false);
  useEffect(() => {
    if (inFlight && !tookInFlight.current) { tookInFlight.current = true; setBuildStartedAt(Date.now() - 1000); }
  }, [inFlight]);

  const sorted = (drafts || []).slice().sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  const newest = sorted[0];
  // Done waiting once a plan newer than the press lands, or the wait runs out.
  const landed = buildStartedAt && newest && new Date(newest.createdAt).getTime() >= buildStartedAt - 5000;
  const building = !!buildStartedAt && !landed && Date.now() - buildStartedAt < FILING_WAIT_MS;

  useEffect(() => {
    if (!buildStartedAt) return undefined;
    if (landed || Date.now() - buildStartedAt >= FILING_WAIT_MS) { setBuildStartedAt(null); return undefined; }
    const t = setInterval(() => { loadDrafts(); }, POLL_MS);
    return () => clearInterval(t);
  }, [buildStartedAt, landed, loadDrafts]);

  const build = async ({ keepLastFiled = false, reveal = false, page = 1 } = {}) => {
    if (building) return;
    const startedAt = Date.now();
    if (reveal) setOpen(true);
    setNothingLeft(false);
    if (!keepLastFiled) setLastFiled(null);
    setBuildStartedAt(startedAt);
    let built = false;
    let threadId = null;
    try {
      const res = await api.post('harvi/brief/filing-plan', { page }, { timeout: 300000 });
      if (res?.skipped) { toast(res.reason || 'The plan could not be built.', 'error'); setBuildStartedAt(null); }
      else if (res?.error) { toast(res.error, 'error'); setBuildStartedAt(null); }
      else if (res?.inProgress) toast(res.reason, 'info');
      else if (res?.built) { built = true; setNoPlanWhy(res.outcome || null); }
      if (res?.conversationId && res.conversationId !== conversationId) { threadId = res.conversationId; setFilingId(threadId); }
    } catch { /* usually the edge timing out while the build carries on — keep waiting */ }
    const list = await loadDrafts(threadId);
    // The turn finished and proposed nothing: there is no read mail left to
    // file. Say so rather than spin until the wait runs out.
    if (built && !list.some(d => new Date(d.createdAt).getTime() >= startedAt - 5000)) {
      setBuildStartedAt(null);
      // A later page coming back empty says nothing about the Inbox as a
      // whole — only that nothing is left past the pages already open. Only
      // page 1 coming back empty marks the Inbox as having nothing to file.
      setNothingLeft(page);
      if (page === 1) writeEmptyAt(conversationId, Date.now());
    }
  };

  // THE NEXT PLAN IS BUILT WITHOUT BEING ASKED. Once per mount, after the
  // drafts have loaded: no plan waiting for review, none built recently, and
  // the last build did not just come back empty. The server refuses a second
  // build while one is running, so two tabs cannot build two plans.
  const autoChecked = useRef(false);
  useEffect(() => {
    if (autoChecked.current || drafts === null || inFlight || buildStartedAt) return;
    autoChecked.current = true;
    const now = Date.now();
    const waiting = drafts.some(d => d.status === 'open');
    const newestAt = drafts.reduce((m, d) => Math.max(m, new Date(d.createdAt).getTime() || 0), 0);
    if (waiting || now - newestAt < FILING_AUTO_GAP_MS || now - readEmptyAt(conversationId) < FILING_AUTO_GAP_MS) return;
    build();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, inFlight, buildStartedAt]);

  // ONCE MAIL IS FILED, THE LIST OF MAIL TO FILE IS STALE — the saved plan
  // described an Inbox that no longer exists. Build the next plan straight
  // away, over whatever read mail is still there. Only when something actually
  // moved: a Save that moved nothing would rebuild the same plan.
  // PAGES ARE THE PLANS STILL OPEN, oldest first: page 1 is the 20 most
  // recent read messages, page 2 the 20 after them, and so on. The card on
  // screen is pinned by id, so a saved page keeps listing what it could not
  // move until the reader pages away from it.
  const [selectedId, setSelectedId] = useState(null);
  const openPlans = (drafts || []).filter(d => d.status === 'open')
    .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
  const pinned = (drafts || []).find(d => d.draftId === selectedId && d.status !== 'expired');
  const pages = pinned && !openPlans.some(d => d.draftId === pinned.draftId)
    ? [...openPlans, pinned].sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)))
    : openPlans;
  const shown = pinned || openPlans[0] || null;
  const pageIdx = shown ? pages.findIndex(d => d.draftId === shown.draftId) : -1;
  const n = shown?.payload?.items?.length || 0;
  const nextPage = openPlans.length + 1;
  const canBuildMore = nextPage <= MAX_FILING_PAGES;

  // A plan that has just landed is the one to show.
  const lastLanded = useRef(null);
  useEffect(() => {
    if (landed && newest && lastLanded.current !== newest.draftId) {
      lastLanded.current = newest.draftId;
      setSelectedId(newest.draftId);
    }
  }, [landed, newest]);

  const nextTwenty = () => build({ reveal: true, page: nextPage });

  // After a Save: if another page is still waiting, go to it; otherwise the
  // Inbox has moved on, so build the next plan straight away.
  const onApplied = (result) => {
    setLastFiled(result);
    if ((result?.moved || 0) <= 0) return;
    const others = openPlans.filter(d => d.draftId !== shown?.draftId);
    if (others.length) setSelectedId(others[0].draftId);
    else build({ keepLastFiled: true, page: 1 });
  };
  // The plan the last result came from: while its own card is on screen the
  // card already shows the result, so the summary is not repeated.
  const pinnedFiledId = useRef(null);

  return (
    <div style={{ borderTop: `1px solid ${B.divider || B.border}` }}>
      <button onClick={() => setOpen(v => !v)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        <span style={{ display: 'inline-flex', color: B.textMd, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
          <Icon name="chevronRight" size={14} />
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: B.text }}>Inbox filing plan</span>
        <span style={{ fontSize: 12, color: B.textLt }}>
          {building ? `— sorting the next ${FILING_BATCH} read messages…` : shown?.status === 'open' ? `— ${n} message${n === 1 ? '' : 's'} ready to review` : nothingLeft ? '— no plan this time' : '— folders for read mail still in your Inbox'}
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 16px' }}>
          {lastFiled && (building || nothingLeft || shown?.draftId !== pinnedFiledId.current) && (
            <FiledSummary result={lastFiled} />
          )}
          {nothingLeft && !building && <NoPlanNote page={nothingLeft} why={noPlanWhy} />}
          {drafts === null ? <Spinner />
            : building ? <Spinner label={`Harvi is sorting the next ${FILING_BATCH} read messages in your Inbox — this takes a minute or two. You can leave this page.`} />
            : shown ? (
                <>
                  <FilingPager index={pageIdx} total={pages.length}
                    onPrev={() => pageIdx > 0 && setSelectedId(pages[pageIdx - 1].draftId)}
                    onNext={() => pageIdx < pages.length - 1 && setSelectedId(pages[pageIdx + 1].draftId)}
                    onMore={nextTwenty} canMore={canBuildMore && pageIdx === pages.length - 1 && !(nothingLeft && nothingLeft === nextPage)} />
                  <AssistantDraftCard key={shown.draftId} draft={shown} onChanged={loadDrafts}
                    onApplied={(r) => { pinnedFiledId.current = shown.draftId; onApplied(r); }} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 6 }}>
                    <span style={{ fontSize: 12, color: B.textLt }}>
                      {shown.status === 'open'
                        ? `Nothing moves until you press Save. This plan expires at ${timeOf(shown.expiresAt)}.`
                        : 'Filed.'}
                    </span>
                    {shown.status !== 'open' && canBuildMore && <Btn variant="ghost" size="sm" onClick={nextTwenty}>Next {FILING_BATCH} messages</Btn>}
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <Muted>
                    {newest?.status === 'saved' ? `Last plan filed at ${timeOf(newest.createdAt)}. The next one builds itself.`
                      : newest?.status === 'expired' ? 'The last plan expired before it was reviewed.'
                      : `Harvi sorts the ${FILING_BATCH} most recent read messages into your folders and deal folders on its own. You review it before anything moves.`}
                  </Muted>
                  <Btn size="sm" onClick={nextTwenty} disabled={building}>Check again</Btn>
                </div>
              )}
        </div>
      )}
    </div>
  );
}

// A build that came back with no plan. "No read mail left" only when Harvi
// actually looked and found none; otherwise what it said, and why the plan
// was refused if propose_mail_filing refused it.
function NoPlanNote({ page, why }) {
  const past = page > 1 ? (page - 1) * FILING_BATCH : 0;
  const looked = why && Number.isFinite(why.searched) ? why.searched : null;
  if (why?.proposeError) {
    return <Muted>Harvi's plan was refused: {why.proposeError} Press Check again to retry.</Muted>;
  }
  if (looked === 0 || (!why && page === 1)) {
    return <Muted>{past ? `No read mail past message ${past} to file.` : 'No read mail left in your Inbox to file.'}</Muted>;
  }
  return (
    <Muted>
      {looked != null ? `Harvi looked at ${looked} read message${looked === 1 ? '' : 's'} and proposed no plan.` : 'Harvi proposed no plan.'}
      {why?.reply ? ` It said: “${why.reply}”` : ''} Press Check again to retry.
    </Muted>
  );
}

// Page n of k over the open plans, and "Next 20" past the last one. The
// messages on page 2 are the 20 after page 1's, so nothing needs saving first.
function FilingPager({ index, total, onPrev, onNext, onMore, canMore }) {
  if (total < 1) return null;
  const btn = (label, onClick, disabled) => (
    <button onClick={onClick} disabled={disabled}
      style={{ background: 'none', border: `1px solid ${B.divider || B.border}`, borderRadius: 8, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: disabled ? B.textLt : B.navy, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit' }}>
      {label}
    </button>
  );
  const from = index * FILING_BATCH + 1;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      {btn('‹ Prev', onPrev, index <= 0)}
      <span style={{ fontSize: 12, color: B.textMd }}>
        Messages {from}–{from + FILING_BATCH - 1} · page {index + 1} of {total}
      </span>
      {index < total - 1
        ? btn('Next ›', onNext, false)
        : btn(`Next ${FILING_BATCH} ›`, onMore, !canMore)}
    </div>
  );
}

function FiledSummary({ result }) {
  const skipped = result.skipped || [];
  return (
    <div style={{ fontSize: 12.5, color: B.textMd, marginBottom: 10 }}>
      {result.message && <div>{result.message}</div>}
      {skipped.length > 0 && (
        <div style={{ marginTop: 3, color: B.amber }}>
          Left in your Inbox:
          <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
            {skipped.map((sk, i) => <li key={i}><strong>{sk.subject}</strong>{sk.reason ? ` — ${sk.reason}` : ''}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MorningBriefWidget;
